/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
import {
	leaseProductData,
	leaseProductParameters,
	vehicleInteriorAndExteriorColorData,
	vehicleOptionsAndAccessoriesData,
} from './leaseProductFlex';

export type LeaseCarData = null | LeaseCarDataResponse | LeaseCarDataResponseOccasion;

export type LeaseCarDataResponse = {
	id: string;
	imageUrl?: ImageUrl;
	vehicleData: VehicleData;
	vehicleInteriorColorData?: VehicleInteriorColorData;
	privateLeaseData: PrivateLeaseData;
	vehicleMetaData: VehicleMetaData;
};

export type LeaseCarDataResponseOccasion = LeaseCarDataResponse & {
	privateLeaseData: PrivateLeaseDataOccasion;
	vehicleMediaData: VehicleMediaData[];
	vehicleMetaData: VehicleMetaDataOccasion;
};

export type LeaseCarDataResponseFlex = LeaseCarDataResponseOccasion & {
	leaseProductData: leaseProductData;
	leaseProductParameters: leaseProductParameters;
	privateLeaseData: PrivateLeaseDataFlex;
	vehicleInteriorAndExteriorColorData: vehicleInteriorAndExteriorColorData[];
	vehicleMetaData: VehicleMetaDataFlex;
	vehicleOptionsAndAccessoriesData: vehicleOptionsAndAccessoriesData[];
};

export type StandardEquipment = {
	descriptions: string[];
	group: string;
	groupCode: string;
	groupId: number;
	name: string;
	schemaCode: string;
	schemaId: number;
	additionalInfo?: {
		seatMainMaterial: string;
		seatAdditionalMaterial: string;
	};
};

export type VehicleData = {
	acceleration: string;
	trimLevel: string;
	batteryCapacity?: string;
	carBodyType: string;
	co2Emission: string;
	commercialName?: string;
	doors: string;
	drivetrain: string;
	energyLabel: string;
	engineCapacity: string;
	enginePowerHP: string;
	enginePowerKW: string;
	fiscalPercentageHigh?: string;
	fiscalPercentageLow?: string;
	frontTypeDiameter: string;
	frontTyreHeightWidthRatio: string;
	frontTyreSpeedIndex: string;
	frontTyreWidth: string;
	fuelConsumptionCombined?: string;
	fuelType: string;
	gearType: string;
	gears: string;
	hasTowingHook: boolean;
	height: string;
	length: string;
	luggageSpace: string;
	make: string;
	maxAllowedWeight: string;
	maxLoadCapacity: string;
	maxTorque?: string;
	maxTrailerWeightBraked: string;
	maxTrailerWeightUnbraked: string;
	model: string;
	numberOfCylinders: string;
	powertrainType?: string;
	range?: string;
	rearTyreDiameter: string;
	rearTyreHeightWidthRatio: string;
	rearTyreSpeedIndex: string;
	rearTyreWidth: string;
	retailPrice: string;
	seats: string;
	standardEquipment: StandardEquipment[];
	topSpeed: string;
	type: string;
	vehicleType: string;
	weight: string;
	width: string;
	year: string;
};

export type VehicleInteriorColorData = {
	name: string;
	id: number;
	group: string;
	groupId: number;
	descriptions: string[];
	color: string;
	type: string;
};

export type ColorType = {
	code: string;
	description: string;
};

export type Color = {
	colorCode: string;
	colorType: ColorType;
	id: number;
	linkedVehicles: number[];
	name: string;
	price: number;
	primaryRgbCode: string;
	primaryRgbHighlightCode: string;
	orderCode: string;
};

export type ModuleComponent = {
	description: string;
	id: number;
	price: number;
};

export type Module = {
	code: string;
	description: string;
	id: number;
	moduleComponents: ModuleComponent[];
	required: boolean;
};

export type Option = {
	description: string;
	foreignOptionId: number;
	id: number;
	linkedVehicles: number[];
	monthPrice: number;
	optionContent?: string;
	status?: string;
};

export type LeaseCarDataPricePoint = {
	description: string;
	duration: number;
	id: number;
	mileage: number;
	pricePerMonth: number;
};

export type ProductData = {
	description: string;
	id: number;
	price: number;
};

export type LeaseCarDataService = {
	description: string;
	id: number;
	price: number;
};

export type Upgrade = {
	altCarType: string;
	description: string;
	foreignOptionId: number;
	id: number;
	linkedVehicles: number[];
	monthPrice: number;
	vehicleData: VehicleData;
};

export type PrivateLeaseData = {
	carId: number;
	colors: Color[];
	modules: Module[];
	options: Option[];
	originalCarId: number;
	pricePoints: LeaseCarDataPricePoint[];
	groupedPricePoints: LeaseCarDurations;
	productData: ProductData[];
	promotionId: number;
	services: LeaseCarDataService[];
	upgrades: Upgrade[];
};

export type PrivateLeaseDataOccasion = PrivateLeaseData & {
	calculationNumber: number;
	offerType: string;
	targetMarkets: string[];
};

export type PrivateLeaseDataFlex = PrivateLeaseData & {
	calculationNumber: number;
	offerType: string;
	targetMarkets: string[];
	pricePoints: LeaseCarDataPricePoint[];
};

export type VehicleMetaData = {
	deliveryTimeInWeeks?: number;
	deliveryTimeExpected?: string;
	usedVehicle: boolean;
};

export type VehicleMetaDataOccasion = VehicleMetaData & {
	licensePlate: string;
	currentMileage: number;
	registrationDate: string; // format "2020-05-25
};

export type VehicleMetaDataFlex = VehicleMetaDataOccasion & {
	registrationDate: string; // format "2020-05-25
	blsCreationDate?: string;
	blsVehicleId?: string;
	categoryId?: string; // format 'FLEX',
	locale?: string;
	vehicleId: string;
	vehicleType?: string; // format 'P',
};

export type VehicleMediaData = {
	type: string;
	url: string;
	isDefault: boolean;
};

export type LeaseCarDurations = {
	[key: number]: LeaseCarDuration;
};

export type LeaseCarDuration = {
	price: number;
	name: string;
	kilometrages: LeaseCarDataPricePoint[];
};

export enum GearType {
	M = 'Handgeschakeld',
	C = 'Automaat',
	I = 'Automaat',
	A = 'Automaat',
	S = 'Handgeschakeld',
	Q = 'Handgeschakeld',
	V = 'Handgeschakeld',
	T = 'Automaat',
}

export type GearTypeKey = keyof typeof GearType;

export enum InteriorColor {
	BGE = 'beige',
	BLK = 'zwart',
	BLU = 'blauw',
	BRN = 'bruin',
	GLD = 'goud',
	GRN = 'groen',
	GRY = 'grijs',
	ORN = 'oranje',
	PNK = 'rose',
	PRP = 'paars',
	RED = 'rood',
	SLV = 'zilver',
	WHT = 'wit',
	YLW = 'geel',
}

export type InteriorColorKey = keyof typeof InteriorColor;

export type ColorInput = {
	/** The name to use for the input */
	label: string;
	/** The value to use for the input */
	value: string;
	/** The color to be shown, in hex-format, including hash (i.e: #ff0000) */
	color?: string;
	/** The colorImage to be shown with the input */
	colorImage?: string;
	/** Only rendered when hasPrices is true;
	 * Optional priceDescription to show with the input */
	priceDescription?: string;
	/** Tile is shown as disabled when set to true */
	disabled?: boolean;
	/** Optional text description to show with the input */
	textDescription?: string;
	/** Optional gradient style is applied on the color tile when set to true */
	gradient?: boolean;
};

export type ImageUrl = {
	w380: string;
	w560: string;
	w760: string;
	w1520: string;
	[key: string]: string;
};
